// SharePoint Service - Updated to use Supabase Edge Functions
// Now uses secure backend authentication with Sites.Selected permissions

import microsoftGraphService from './microsoftGraphService';

class SharePointService {
  constructor() {
    this.siteId = null;
    this.driveId = null;
    this.libraryName = 'Documents';
  }

  /**
   * Set SharePoint site ID for document operations
   */
  setSiteId(siteId) {
    this.siteId = siteId;
  }

  /**
   * Set SharePoint drive ID for direct drive access
   */
  setDriveId(driveId) {
    this.driveId = driveId;
  }

  /**
   * Get user's SharePoint sites via Edge Function
   */
  async getUserSites() {
    try {
      const response = await microsoftGraphService?.getSharePointSites();
      return response?.value || [];
    } catch (error) {
      console.error('Error fetching SharePoint sites:', error);
      throw error;
    }
  }

  /**
   * Get document libraries for a SharePoint site
   */
  async getDocumentLibraries(siteId = this.siteId) {
    if (!siteId) {
      throw new Error('Site ID is required');
    }

    try {
      const response = await microsoftGraphService?.getSiteDocumentLibraries(siteId);
      return response?.value || [];
    } catch (error) {
      console.error('Error fetching document libraries:', error);
      throw error;
    }
  }

  /**
   * Upload document to SharePoint
   */
  async uploadDocument(file, fileName, folderPath = '') {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set before uploading');
    }

    try {
      const uploadedFile = await microsoftGraphService?.uploadDocument(
        this.siteId,
        this.driveId,
        file,
        fileName,
        folderPath
      );

      // Get edit URL
      const editUrl = await this.getDocumentEditUrl(uploadedFile?.id);

      return {
        ...uploadedFile,
        editUrl,
        webUrl: uploadedFile?.webUrl,
      };
    } catch (error) {
      console.error('Error uploading to SharePoint:', error);
      throw error;
    }
  }

  /**
   * Get direct edit URL for a document
   */
  async getDocumentEditUrl(itemId) {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set');
    }

    try {
      return await microsoftGraphService?.getDocumentEditUrl(
        this.siteId,
        this.driveId,
        itemId
      );
    } catch (error) {
      console.error('Error getting edit URL:', error);
      throw error;
    }
  }

  /**
   * List documents in SharePoint library
   */
  async listDocuments(folderPath = '') {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set');
    }

    try {
      return await microsoftGraphService?.listDocuments(
        this.siteId,
        this.driveId,
        folderPath
      );
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  }

  /**
   * Create folder in SharePoint library
   */
  async createFolder(folderName, parentPath = '') {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set');
    }

    try {
      return await microsoftGraphService?.createFolder(
        this.siteId,
        this.driveId,
        folderName,
        parentPath
      );
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }

  /**
   * Delete document from SharePoint
   */
  async deleteDocument(itemId) {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set');
    }

    try {
      console.log('[SHAREPOINT] Attempting to delete document:', itemId);
      return await microsoftGraphService?.deleteDocument(
        this.siteId,
        this.driveId,
        itemId
      );
    } catch (error) {
      console.error('[SHAREPOINT] Error deleting document:', error);
      
      // Preserve the original error for proper handling upstream
      // Don't modify 404 errors - let the documentService handle them
      throw error;
    }
  }

  /**
   * Get document download URL
   */
  async getDownloadUrl(itemId) {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set');
    }

    try {
      const item = await microsoftGraphService?.callGraphAPI(
        `/sites/${this.siteId}/drives/${this.driveId}/items/${itemId}`
      );
      return item?.['@microsoft.graph.downloadUrl'];
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  /**
   * Search for documents in SharePoint
   */
  async searchDocuments(query) {
    if (!this.siteId || !this.driveId) {
      throw new Error('Site ID and Drive ID must be set');
    }

    try {
      return await microsoftGraphService?.searchDocuments(
        this.siteId,
        this.driveId,
        query
      );
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  }

  /**
   * Download document from SharePoint
   * @param {string} itemId - SharePoint item ID
   * @param {string} siteId - Optional site ID (falls back to this.siteId)
   * @param {string} driveId - Optional drive ID (falls back to this.driveId, or uses default drive)
   */
  async downloadDocument(itemId, siteId = null, driveId = null) {
    // Use provided parameters or fall back to instance properties
    let targetSiteId = siteId || this.siteId;
    let targetDriveId = driveId || this.driveId;

    // Try to get from localStorage as fallback
    if (!targetSiteId || !targetDriveId) {
      try {
        const siteStr = localStorage.getItem('zentri_sharepoint_site');
        const driveStr = localStorage.getItem('zentri_sharepoint_drive');
        
        if (siteStr) {
          const site = JSON.parse(siteStr);
          if (site?.id) {
            targetSiteId = targetSiteId || site.id;
            console.log('[SHAREPOINT] Using siteId from localStorage:', targetSiteId);
          }
        }
        
        if (driveStr) {
          const drive = JSON.parse(driveStr);
          if (drive?.id) {
            targetDriveId = targetDriveId || drive.id;
            console.log('[SHAREPOINT] Using driveId from localStorage:', targetDriveId);
          }
        }
      } catch (localStorageError) {
        console.warn('[SHAREPOINT] Could not read from localStorage:', localStorageError);
      }
    }

    if (!targetSiteId) {
      throw new Error('Site ID must be set before downloading. Please configure SharePoint in System Settings.');
    }

    try {
      console.log('[SHAREPOINT] Downloading document:', itemId);
      console.log('[SHAREPOINT] Using siteId:', targetSiteId);
      console.log('[SHAREPOINT] Using driveId:', targetDriveId || 'default drive');

      let item;
      
      // If we have a drive ID, use the specific drive endpoint
      if (targetDriveId) {
        // Get item metadata including download URL via Microsoft Graph
        item = await microsoftGraphService?.callGraphAPI(
          `/sites/${targetSiteId}/drives/${targetDriveId}/items/${itemId}`
        );
      } else {
        // Fallback: Use default drive endpoint (like PowerShell script)
        // First, try to get the default drive
        try {
          const drives = await microsoftGraphService?.callGraphAPI(
            `/sites/${targetSiteId}/drives`
          );
          
          if (drives?.value && drives.value.length > 0) {
            // Use the first drive (usually the default Documents library)
            targetDriveId = drives.value[0].id;
            console.log('[SHAREPOINT] Using default drive:', targetDriveId);
            
            item = await microsoftGraphService?.callGraphAPI(
              `/sites/${targetSiteId}/drives/${targetDriveId}/items/${itemId}`
            );
          } else {
            // Last resort: try the default drive endpoint directly
            console.log('[SHAREPOINT] Attempting to use default drive endpoint');
            item = await microsoftGraphService?.callGraphAPI(
              `/sites/${targetSiteId}/drive/items/${itemId}`
            );
          }
        } catch (driveError) {
          // If getting drives fails, try default drive endpoint directly
          console.log('[SHAREPOINT] Falling back to default drive endpoint');
          item = await microsoftGraphService?.callGraphAPI(
            `/sites/${targetSiteId}/drive/items/${itemId}`
          );
        }
      }

      if (!item?.['@microsoft.graph.downloadUrl']) {
        throw new Error('Download URL not available for this document');
      }

      // Download the actual file content using the Microsoft Graph download URL
      const downloadResponse = await fetch(item?.['@microsoft.graph.downloadUrl']);
      
      if (!downloadResponse?.ok) {
        throw new Error(`Failed to download file content: ${downloadResponse.status} ${downloadResponse.statusText}`);
      }

      const blob = await downloadResponse?.blob();
      console.log('[SHAREPOINT] Download successful, blob size:', blob?.size);
      
      return blob;
    } catch (error) {
      console.error('[SHAREPOINT] Error downloading document:', error);
      throw error;
    }
  }
}

export default new SharePointService();